// "Hold to snap": recognise a rough pen stroke as a line, circle, box, triangle, diamond or polygon.
import { type Box, type Vec, boxFromPoints, dist, distToSegment, pathLength, simplifyIndices } from "./geometry";

export type Recognized =
  | { kind: "line"; a: Vec; b: Vec }
  | { kind: "polyline"; pts: Vec[] }
  | { kind: "shape"; shape: "rect" | "ellipse" | "triangle" | "diamond"; box: Box }
  | { kind: "polygon"; pts: Vec[] };

function angleAt(a: Vec, b: Vec, c: Vec): number {
  const v1 = { x: a.x - b.x, y: a.y - b.y };
  const v2 = { x: c.x - b.x, y: c.y - b.y };
  const d = Math.hypot(v1.x, v1.y) * Math.hypot(v2.x, v2.y);
  if (!d) return Math.PI;
  return Math.acos(Math.max(-1, Math.min(1, (v1.x * v2.x + v1.y * v2.y) / d)));
}

/** Snap a nearly horizontal / vertical / 45° line exactly. */
function snapLine(a: Vec, b: Vec): Vec {
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const step = Math.PI / 4;
  const snapped = Math.round(ang / step) * step;
  if (Math.abs(snapped - ang) > (6 * Math.PI) / 180) return b;
  const l = dist(a, b);
  return { x: a.x + Math.cos(snapped) * l, y: a.y + Math.sin(snapped) * l };
}

export function recognize(raw: Vec[]): Recognized | null {
  if (raw.length < 4) return null;
  const box = boxFromPoints(raw);
  const diag = Math.hypot(box.w, box.h);
  if (diag < 12) return null;
  const total = pathLength(raw);
  const first = raw[0];
  const last = raw[raw.length - 1];
  const gap = dist(first, last);

  // straight line: every point stays close to the chord
  let maxDev = 0;
  for (const p of raw) maxDev = Math.max(maxDev, distToSegment(p, first, last));
  if (gap > diag * 0.6 && maxDev < Math.max(4, gap * 0.07) && total < gap * 1.25) {
    return { kind: "line", a: first, b: snapLine(first, last) };
  }

  const closed = gap < Math.max(18, total * 0.18);
  if (!closed) {
    // open polyline with a few clear corners (e.g. an angle bracket)
    const keep = simplifyIndices(raw, diag * 0.06).map((i) => raw[i]);
    if (keep.length >= 3 && keep.length <= 5) return { kind: "polyline", pts: keep };
    return null;
  }

  // ellipse test: normalised radius stays near 1
  const cx = box.x + box.w / 2;
  const cy = box.y + box.h / 2;
  let dev = 0;
  for (const p of raw) {
    const r = Math.hypot((p.x - cx) / (box.w / 2 || 1), (p.y - cy) / (box.h / 2 || 1));
    dev += Math.abs(r - 1);
  }
  dev /= raw.length;

  // polygon test on a simplified closed outline
  const loop = [...raw, first];
  let idx = simplifyIndices(loop, diag * 0.07);
  let corners = idx.map((i) => loop[i]);
  if (corners.length > 1 && dist(corners[0], corners[corners.length - 1]) < diag * 0.12) corners = corners.slice(0, -1);
  // drop nearly straight "corners"
  let changed = true;
  while (changed && corners.length > 3) {
    changed = false;
    for (let i = 0; i < corners.length; i++) {
      const a = corners[(i - 1 + corners.length) % corners.length];
      const b = corners[i];
      const c = corners[(i + 1) % corners.length];
      if (angleAt(a, b, c) > (155 * Math.PI) / 180) {
        corners.splice(i, 1);
        changed = true;
        break;
      }
    }
  }
  idx = [];

  if (corners.length === 3 && dev > 0.1) {
    // triangle: upright isosceles if it looks like one, else the exact triangle
    const top = corners.reduce((m, p) => (p.y < m.y ? p : m));
    const others = corners.filter((p) => p !== top);
    const baseFlat = Math.abs(others[0].y - others[1].y) < box.h * 0.2;
    const apexCentred = Math.abs(top.x - (others[0].x + others[1].x) / 2) < box.w * 0.18;
    if (baseFlat && apexCentred) return { kind: "shape", shape: "triangle", box };
    return { kind: "polygon", pts: corners };
  }

  if (corners.length === 4 && dev > 0.08) {
    // axis-aligned box, or a diamond standing on a corner
    const axisAligned = corners.every((p) => {
      const nearX = Math.min(Math.abs(p.x - box.x), Math.abs(p.x - box.x - box.w)) < box.w * 0.2;
      const nearY = Math.min(Math.abs(p.y - box.y), Math.abs(p.y - box.y - box.h)) < box.h * 0.2;
      return nearX && nearY;
    });
    if (axisAligned) return { kind: "shape", shape: "rect", box };
    const onMidpoints = corners.every((p) => {
      const midX = Math.abs(p.x - cx) < box.w * 0.18;
      const midY = Math.abs(p.y - cy) < box.h * 0.18;
      return midX !== midY;
    });
    if (onMidpoints) return { kind: "shape", shape: "diamond", box };
    return { kind: "polygon", pts: corners };
  }

  if (dev < 0.16) return { kind: "shape", shape: "ellipse", box };

  if (corners.length >= 5 && corners.length <= 8) return { kind: "polygon", pts: corners };
  return null;
}

/** Evenly spaced points along a polyline (so a snapped stroke renders with crisp straight edges). */
export function resample(pts: Vec[], spacing: number, closed = false): Vec[] {
  const src = closed ? [...pts, pts[0]] : pts;
  const out: Vec[] = [src[0]];
  for (let i = 1; i < src.length; i++) {
    const a = src[i - 1];
    const b = src[i];
    const l = dist(a, b);
    const n = Math.max(1, Math.round(l / spacing));
    for (let k = 1; k <= n; k++) out.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n });
  }
  return out;
}
